import { useState } from 'react';
import { Collapse, List, Radio, Button } from 'antd';

import { ChevronSVG } from 'components/iconsSVG/chevron';
import { filterGrid, tagClassNames, filterColors, sorts, type TSort } from 'utils/filters';
import { useI18n } from 'context/i18n';
import { FilterTag } from 'components/filterTag';
import type { TFilter, TFilterSection } from 'types/product-list';
import type { FilterState } from './use-filters';
import { getCountryFromMarket } from 'utils/translations';
import { useLocale } from 'hooks';

import styles from '../filterPanel/style.module.css';

const { Panel } = Collapse;

type CamperFilterPanelProps = {
  filters: TFilterSection[];
  state: FilterState;
  filtersCount: number;
  onApply: (filters: FilterState['active'], sort?: TSort) => void;
  onClose: (visible: boolean, fakeApply?: boolean) => void;
  onRemove: (params: { parent: string; key: string }) => void;
  onSort: (sort: TSort) => void;
  onReset: () => void;
};

export function CamperFilterPanel({ filters, state, filtersCount, onApply, onSort, onClose, onReset, onRemove }: CamperFilterPanelProps) {
  const { t } = useI18n();
  const { country: market } = useLocale();
  const country = getCountryFromMarket(market);

  const [active, setActive] = useState(Object.keys(state.active));

  const handleClickFilter = ({ parent, filter, multiple }: { parent: string; filter: TFilter; multiple: boolean }) => {
    const currentFilters = { ...state.active };
    // si no existe el parent lo creamos
    if (!currentFilters[parent]) {
      currentFilters[parent] = new Map<string, TFilter>();
    }
    if (!multiple) {
      // si no es multiple lo vaciamos y añadimos el filtro
      if (currentFilters[parent].size > 0) {
        currentFilters[parent].clear();
      }
      currentFilters[parent].set(filter.key, filter);
    }
    if (multiple) {
      // si es multiple, si ya existe el filtro lo eliminamos, si no lo añadimos
      if (currentFilters[parent].has(filter.key)) {
        currentFilters[parent].delete(filter.key);
      } else {
        currentFilters[parent].set(filter.key, filter);
      }
    }
    onApply(currentFilters, state.sort);
  };

  return (
    <>
      {/* @ts-ignore !antd!! */}
      <Collapse
        bordered={false}
        activeKey={active}
        expandIcon={({ isActive }) => (
          <span className={isActive ? styles.chevronActive : ''}>
            <ChevronSVG />
          </span>
        )}
        expandIconPosition="right"
        className={styles.filterCollapse}
        onChange={(key) => {
          setActive(Array.isArray(key) ? key : [key]);
        }}
      >
        <Panel
          className={styles.mobileSort}
          showArrow={false}
          collapsible="disabled"
          key="filtersApplied"
          data-hidden={filtersCount <= 0}
          header={
            <div className={`${styles.appliedFiltersPanel} ${styles.customFilterItem}`}>
              {Object.keys(state.active).map((filterKey) => {
                if (filterKey !== 'first') {
                  return Array.from(state.active[filterKey]).map(([_, appliedFilter]) => (
                    <FilterTag
                      key={`${filterKey}.${appliedFilter.key}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onRemove({ parent: filterKey, key: appliedFilter.key });
                      }}
                      checked={true}
                      color={filterKey === 'colors' ? filterColors[appliedFilter.key] : null}
                      label={appliedFilter.label !== '' ? appliedFilter.label : appliedFilter.urlParam}
                    />
                  ));
                }
                return null;
              })}
            </div>
          }
        />
        {/* @ts-ignore antd!! */}
        <Panel className={styles.desktopSort} key="sortby" header={t('menu.lateral', 'ordenar.por')}>
          {Object.keys(sorts).map((sortValue) => (
            <Radio
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onSort(sortValue as TSort);
              }}
              key={sortValue}
              className={styles.sortRadio}
              checked={state.sort === sortValue}
            >
              {t('listado.productos', sorts[sortValue])}
            </Radio>
          ))}
        </Panel>
        {
          // quitamos collections por ahora (feedback bpico, pedido en su Excel - 16/08/2021)
          filters.reduce((acc, { title, id, options, multiple }) => {
            if (id === 'collections') {
              return acc;
            }
            acc.push(
              // @ts-ignore antd!!
              <Panel key={id} header={id !== 'sizes' ? t(...title) : `${t(...title)} (${country})`} className={`${id === 'sizes' ? styles.sizesPanel : ''}`}>
                <List
                  grid={filterGrid[id] || filterGrid.default}
                  dataSource={options}
                  locale={{ emptyText: 'No available filters' }}
                  renderItem={(filter) => {
                    // const checked = appliedFilters[id] && appliedFilters[id].filter((opt) => opt.key === filter.key).length > 0;
                    const checked = state.active[id] && state.active[id].has(filter.key);
                    const available = state.available[id] && state.available[id].has(filter.key);
                    return (
                      <List.Item>
                        <div className={styles.customFilterItem}>
                          <FilterTag
                            onClick={() =>
                              handleClickFilter({
                                parent: id,
                                filter,
                                multiple,
                              })
                            }
                            checked={checked}
                            color={id === 'colors' ? filterColors[filter.key] : null}
                            label={filter.label !== '' ? filter.label : filter.urlParam}
                            type={Object.keys(tagClassNames).includes(id) ? id : undefined}
                            available={available}
                          />
                        </div>
                      </List.Item>
                    );
                  }}
                />
              </Panel>,
            );
            return acc;
          }, [])
        }
        <Panel
          showArrow={false}
          collapsible="disabled"
          className={styles.collapseFooter}
          key="filtersFooter"
          header={
            <div className={styles.filterPanelFooter}>
              <Button onClick={() => onClose(false)}>{t('generico', 'boton.cerrar', 'Close')}</Button>
              <div></div>
              <Button onClick={onReset}>{t('generico', 'boton.reiniciar', 'Reset')}</Button>
              <Button type="primary" onClick={() => onClose(false, true)}>
                {t('generico', 'boton.aplicar', 'Apply')}
              </Button>
            </div>
          }
        />
      </Collapse>
    </>
  );
}
